import React, { useEffect, useMemo, useState } from 'react';
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@routes-plan/ui';
import { formatCurrency } from '../../../utils/format/currency';
import { Loan } from '../../../models/loan';
import axios from 'axios';
import moment from 'moment-timezone';
import PermissionButton from '../../../components/common/PermissionButton';
import { operationalTimezone } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';
import { dailyLimitMessage } from '../../../utils/dailyLimit';

interface PaysModalWebProps {
  open: boolean;
  onClose: () => void;
  client: Loan | null;
  clientId?: number;
  canAllowDiscount: boolean;
  canAllowManualDiscount: boolean;
  canAllowAutomaticDiscount: boolean;
  canPdf: boolean;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  onPaymentSuccess: () => void;
  /** El abono dejó el préstamo pagado: se ofrece calificar al cliente. */
  onLoanClosed?: (loanId: number, clientName: string | null) => void;
}

type RouteDiscountSettings = {
  discountDays?: number | string;
  allowManualDiscount?: boolean | number | string;
};

const parseAmount = (value: string | number | null | undefined) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = String(value ?? '').trim().replace(',', '.');
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeDiscountDays = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
};

const getLoanAgeDays = (loanDate: unknown) => {
  if (!loanDate) return null;

  const parsedDate = new Date(String(loanDate));
  if (Number.isNaN(parsedDate.getTime())) return null;

  const today = new Date();
  const utcStart = Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  );
  const utcToday = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return Math.max(0, Math.floor((utcToday - utcStart) / 86400000));
};

const PaymentModal: React.FC<PaysModalWebProps> = ({
  open,
  onClose,
  client,
  clientId,
  canAllowDiscount,
  canAllowManualDiscount,
  canAllowAutomaticDiscount,
  canPdf,
  showAlert,
  showLoading,
  hideLoading,
  onPaymentSuccess,
  onLoanClosed,
}) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal estándar.
  const { loading: modalBusy, showLoading: showModalLoading, hideLoading: hideModalLoading } = useModalLoading();
  const [valorAbono, setValorAbono] = useState('0');
  const [valorDescuento, setValorDescuento] = useState('0');
  const [selectedFormPay, setSelectedFormPay] = useState('1');
  const [selectedBank, setSelectedBank] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [saldoRestante, setSaldoRestante] = useState(client?.balance || 0);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [location, setLocation] = useState<string | null>(null);
  const [banks, setBanks] = useState(Array<{ id: number; name: string }>());
  const [discountEnabled, setDiscountEnabled] = useState(false);

  const routeSettings = useMemo(
    () => ((client?.route ?? null) as RouteDiscountSettings | null),
    [client],
  );
  const baseBalance = Number(client?.balance || 0);
  const discountDays = normalizeDiscountDays(routeSettings?.discountDays);
  const canUseManualDiscount = canAllowManualDiscount;
  const canUseAutomaticDiscount = canAllowAutomaticDiscount;
  const canUseAnyDiscount =
    canAllowDiscount || canUseManualDiscount || canUseAutomaticDiscount;
  const loanAgeDays = getLoanAgeDays(client?.date);
  const utilityValue = Math.max(0, Number((client as any)?.interestValue ?? 0));
  const canApplyCalculatedDiscount =
    canUseAutomaticDiscount &&
    loanAgeDays !== null &&
    loanAgeDays <= discountDays &&
    utilityValue > 0;
  const calculatedDiscountAmount = discountEnabled
    ? Math.max(0, utilityValue * 0.5)
    : 0;

  const resetForm = () => {
    setValorAbono('0');
    setValorDescuento('0');
    setSelectedFormPay('1');
    setSelectedBank('');
    setReceiptNumber('');
    setSaldoRestante(client?.balance || 0);
    setErrorMessage('');
    setIsPaymentComplete(false);
    setDiscountEnabled(false);
  };

  const fetchCoordinates = async () => {
    try {
      const coords = await getCoordinates();
      setLocation(coords);
    } catch (error) {
      showAlert(t('payments.gpsRequired'), 'error');
    }
  };

  const fetchBanks = async () => {
    try {
      const { data } = await axios.get('/banks');
      setBanks(data.data);
    } catch (error) {
      showAlert(t('payments.banksLoadError'), 'error');
    }
  };

  useEffect(() => {
    fetchBanks();
    fetchCoordinates();
  }, []);

  useEffect(() => {
    if (open && client) {
      setValorAbono('0');
      setValorDescuento('0');
      setSelectedFormPay('1');
      setSelectedBank('');
      setReceiptNumber('');
      setSaldoRestante(client?.balance || 0);
      setErrorMessage('');
      setIsPaymentComplete(false);
      setDiscountEnabled(false);
    }
  }, [open, client, canAllowDiscount, canAllowManualDiscount, canAllowAutomaticDiscount]);

  useEffect(() => {
    if (!canApplyCalculatedDiscount && discountEnabled) {
      setDiscountEnabled(false);
      setValorDescuento('0');
    }
  }, [canApplyCalculatedDiscount, discountEnabled]);

  useEffect(() => {
    if (canApplyCalculatedDiscount && discountEnabled) {
      setValorDescuento(String(Math.max(0, utilityValue * 0.5)));
    }
  }, [canApplyCalculatedDiscount, discountEnabled, utilityValue]);

  useEffect(() => {
    if (canApplyCalculatedDiscount && discountEnabled) {
      setValorAbono(String(Math.max(0, baseBalance - Math.max(0, utilityValue * 0.5))));
    }
  }, [baseBalance, canApplyCalculatedDiscount, discountEnabled, utilityValue]);

  useEffect(() => {
    if (!client) return;

    const abonoValue = parseAmount(valorAbono);
    let discountValue = 0;

    if (canUseAnyDiscount) {
      if (canUseManualDiscount) {
        const rawDiscount = Math.min(
          Math.max(0, parseAmount(valorDescuento)),
          baseBalance,
        );
        discountValue = Math.min(rawDiscount, Math.max(0, baseBalance - abonoValue));
      } else {
        discountValue = canApplyCalculatedDiscount && discountEnabled
          ? Math.max(0, utilityValue * 0.5)
          : 0;
      }
    }

    const maxForAbono = Math.max(0, baseBalance - discountValue);
    if (abonoValue > maxForAbono) {
      setValorAbono(String(maxForAbono));
      setSaldoRestante(Math.max(0, baseBalance - maxForAbono - discountValue));
      return;
    }

    setSaldoRestante(Math.max(0, baseBalance - abonoValue - discountValue));
  }, [
    baseBalance,
    canApplyCalculatedDiscount,
    canUseAnyDiscount,
    canUseManualDiscount,
    client,
    discountEnabled,
    utilityValue,
    valorAbono,
    valorDescuento,
  ]);

  const handleAbonoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    const abono = parseAmount(nextValue);
    const rawManualDiscount = Math.min(
      Math.max(0, parseAmount(valorDescuento)),
      baseBalance,
    );
    const discountValue = canUseAnyDiscount
      ? canUseManualDiscount
        ? rawManualDiscount
        : canApplyCalculatedDiscount && discountEnabled
          ? Math.max(0, utilityValue * 0.5)
          : 0
      : 0;
    const maxValue = Math.max(0, baseBalance - discountValue);
    const nuevoSaldo = baseBalance - (abono + discountValue);

    if (abono > maxValue) {
      setErrorMessage(
        canUseManualDiscount
          ? t('payments.sumExceedsBalance')
          : t('payments.valueExceedsBalance'),
      );
      setValorAbono(String(maxValue));
      setSaldoRestante(Math.max(0, baseBalance - maxValue - discountValue));
    } else {
      setErrorMessage('');
      setValorAbono(nextValue);
      setSaldoRestante(Math.max(0, nuevoSaldo));
    }
  };

  const handleDescuentoManualChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canUseManualDiscount) {
      return;
    }

    const nextValue = event.target.value;
    let descuento = Math.min(
      Math.max(0, parseAmount(nextValue)),
      baseBalance,
    );
    const abono = parseAmount(valorAbono);

    if (abono + descuento > baseBalance) {
      descuento = Math.max(0, baseBalance - abono);
      setErrorMessage(t('payments.sumExceedsBalance'));
    } else {
      setErrorMessage('');
    }

    setValorDescuento(String(descuento));
    setSaldoRestante(Math.max(0, baseBalance - abono - descuento));
  };

  const handleAccept = async () => {
    setIsSaving(true);

    if (!location) {
      showAlert(t('payments.gpsInactive'), 'warning');
      setIsSaving(false);
      return;
    }

    if (selectedFormPay === '0' && (!selectedBank || !receiptNumber)) {
      setErrorMessage(t('payments.bankRequired'));
      setIsSaving(false);
      return;
    }

    const currentDateTime = moment().tz(operationalTimezone()).format('YYYY-MM-DD HH:mm:ss');
    const paymentAbono = parseAmount(valorAbono);
    const appliedDiscountTotal = canUseAnyDiscount
      ? canUseManualDiscount
        ? Math.min(
            Math.max(0, parseAmount(valorDescuento)),
            baseBalance,
            Math.max(0, baseBalance - paymentAbono),
          )
        : canApplyCalculatedDiscount && discountEnabled
          ? Math.max(0, utilityValue * 0.5)
          : 0
      : 0;

    if (paymentAbono + appliedDiscountTotal > baseBalance) {
      setErrorMessage(t('payments.sumExceedsBalance'));
      setIsSaving(false);
      return;
    }

    const formData = {
      loanId: Number(clientId),
      location,
      userId: 1,
      bankId: selectedFormPay === '0' ? selectedBank : null,
      bankReceipt: selectedFormPay === '0' ? receiptNumber : null,
      balance: Math.max(0, baseBalance - paymentAbono - appliedDiscountTotal),
      cash: selectedFormPay === '1',
      value: valorAbono,
      discountValue: appliedDiscountTotal,
      state: true,
      paymentDate: currentDateTime,
      operationDate: currentDateTime,
      invoice: `INV-${clientId}-${Date.now()}`,
      visitOrderId: 1,
    };

    try {
      showModalLoading();
      await axios.post('payments', formData);
      showAlert(t('payments.savedOk'), 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsPaymentComplete(true);
      onPaymentSuccess();
      onClose();
      if (formData.balance <= 0 && Number(clientId) > 0) onLoanClosed?.(Number(clientId), client?.client?.fullName ?? null);
    } catch (error) {
      showAlert(dailyLimitMessage(error, t) ?? t('payments.saveError'), dailyLimitMessage(error, t) ? 'warning' : 'error');
    } finally {
      setIsSaving(false);
      hideModalLoading();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const coordinates = `${lat}, ${lng}`;
            resolve(coordinates);
          },
          error => {
            reject(error);
          }
        );
      } else {
        showAlert(t('payments.geoUnsupported'), 'warning');
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('payments.modalTitle')}
      maxWidth={500}
      loading={modalBusy}
      composed
    >
      <StandardModalBody>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {client?.client.fullName ?? ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('payments.modalSubtitle', { loan: clientId ?? '', idNumber: client?.client.idNumber ?? '' })}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(130px, 100%), 1fr))',
            gap: 2,
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">{t('payments.balanceLabel')}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{formatCurrency(client?.balance ?? 0)}</Typography>
          </Box>
          {(parseAmount(valorAbono) > 0 || parseAmount(valorDescuento) > 0) && (
            <Box>
              <Typography variant="caption" color="text.secondary" component="div">{t('payments.newBalanceLabel')}</Typography>
              <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>{formatCurrency(saldoRestante)}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">{t('payments.daysLate')}</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{client?.daysLate ?? 0}</Typography>
          </Box>
        </Box>
        {canUseAutomaticDiscount && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('payments.loanAgeLine', { days: loanAgeDays ?? t('payments.notAvailable') })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('payments.discountBaseLine', { value: formatCurrency(utilityValue) })}
            </Typography>
          </Box>
        )}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{t('payments.paymentMethod')}</InputLabel>
          <Select
            value={selectedFormPay}
            onChange={(e) => setSelectedFormPay(e.target.value as string)}
            label={t('payments.paymentMethod')}
            disabled={isPaymentComplete}
          >
            <MenuItem value="1">{t('payments.cash')}</MenuItem>
            <MenuItem value="0">{t('payments.transfer')}</MenuItem>
          </Select>
        </FormControl>
        {selectedFormPay === '0' && (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t('payments.bank')}</InputLabel>
              <Select
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value as string)}
                label={t('payments.bank')}
                disabled={isPaymentComplete}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label={t('payments.receiptNumberLabel')}
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              sx={{ mb: 2 }}
              disabled={isPaymentComplete}
            />
          </>
        )}
        <TextField
          fullWidth
          size="small"
          inputProps={{ inputMode: 'decimal' }}
          label={t('payments.paymentValue')}
          value={valorAbono}
          onChange={handleAbonoChange}
          onFocus={() => {
            if (valorAbono === '0') setValorAbono('');
          }}
          onBlur={() => {
            if (valorAbono.trim() === '') setValorAbono('0');
          }}
          sx={{ mb: 2 }}
          disabled={isPaymentComplete || (canUseAutomaticDiscount && discountEnabled)}
        />
        {canUseManualDiscount && baseBalance > 0 && (
          <TextField
            fullWidth
            size="small"
            label={t('payments.discountValue')}
            value={valorDescuento}
            onChange={handleDescuentoManualChange}
            onFocus={() => {
              if (valorDescuento === '0') setValorDescuento('');
            }}
            onBlur={() => {
              if (valorDescuento.trim() === '') setValorDescuento('0');
            }}
            sx={{ mb: 2 }}
            disabled={isPaymentComplete}
            helperText={t('payments.manualDiscountHelp')}
          />
        )}
        {canApplyCalculatedDiscount && (
          <>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={(
                <Checkbox
                  checked={discountEnabled}
                  onChange={(event) => {
                    const nextValue = event.target.checked;
                    setDiscountEnabled(nextValue);
                    if (!nextValue) {
                      setValorDescuento('0');
                    }
                  }}
                  disabled={isPaymentComplete}
                />
              )}
              label={t('payments.discount')}
            />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('payments.calculatedDiscountLine', { value: formatCurrency(Math.max(0, utilityValue * 0.5)) })}
            </Typography>
            {discountEnabled && (
              <TextField
                fullWidth
                size="small"
                label={t('payments.discountValue')}
                value={valorDescuento}
                sx={{ mb: 2 }}
                disabled
              />
            )}
          </>
        )}

      </StandardModalBody>
      <StandardModalFooter>
        {!isPaymentComplete && (
          <>
            <Button variant="outlined" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleAccept}
              loading={isSaving}
            >
              {t('payments.saveAction')}
            </LoadingButton>
          </>
        )}
        {isPaymentComplete && (
          <>
            <Button variant="outlined" onClick={handleClose}>
              {t('common.close')}
            </Button>
            <PermissionButton
              allowed={canPdf}
              deniedTooltip={t('payments.noPermissionPdf')}
              tooltip={t('payments.sendPdf')}
              variant="contained"
              onClick={() => showAlert(t('payments.pdfGenerated'), 'info')}
            >
              {t('payments.sendPdf')}
            </PermissionButton>
          </>
        )}

      </StandardModalFooter>
    </StandardModal>
  );
};

export default PaymentModal;
